import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import {
  formatReportTypeColumnLabel,
  resolveReportSaleAmount,
} from "../../../utils/reports/reportDisplayLabels";
import {
  pickHoldingBenchmarkFields,
  withTxnBenchmarkFields,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import { formatBuySellTxnTypeLabel } from "../../../utils/transactions/txnTypeDisplayLabel";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import {
  PMS_CAP_REPORT,
  PMS_CONSOLIDATED_REPORT,
  PMS_SECTOR_REPORT,
} from "./pmsReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveCurrentDateOrToday = (row: Record<string, any>): string =>
  formatEpochDate(row.currentDate ?? row.current_date ?? Date.now());

const getPmsSchemeName = (row: Record<string, any>): string =>
  String(row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ??
    holding.transaction ??
    holding.txns ??
    holding.trxn ??
    [];
  return Array.isArray(list) ? list : [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ??
      row.txnType ??
      row.transactionType ??
      row.transactiontype ??
      "",
  ).trim();
  if (!raw) return false;
  return isEquitySellType(raw) || raw.toUpperCase() === "SELL";
};

const filterPmsHoldings = (rows: any[]): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const toNum = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "" || v === "-") return null;
    const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  return rows.filter((row) => {
    if (!row || typeof row !== "object") return true;
    const units = toNum(row.units ?? row.quantity ?? row.qty);
    if (units === null) return true;
    if (units > 0) return true;
    const invested = toNum(row.investedAmount ?? row.amount ?? row.costValue);
    return invested != null && invested > 0;
  });
};

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrPms"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    row.avgDays ??
    row.totalTimeInDays ??
    meta.averageDays ??
    meta.averageDay ??
    meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const normalizePmsReportTotalHoldingInfo = (
  info: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null => {
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLossPercentage:
      info.gainLossPercentage ?? info.gainLossPerc ?? info.roi,
    cagrPms: info.cagrPms ?? info.cagr,
    cagr: info.cagr ?? info.cagrPms,
  };
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getPmsSchemeName(row);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      inceptionDate: formatEpochDate(
        row.inceptionDate ?? row.purchaseDate ?? meta.inceptionDate,
      ),
      asOfDate: formatEpochDate(row.asOfDate ?? meta.asOfDate),
      currentDate: resolveCurrentDateOrToday(row),
      investedAmount: row.investedAmount ?? row.amount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue ?? row.marketValue,
      gainLoss:
        row.gainLoss ??
        row.unrealisedGainLoss ??
        row.unrealisedPNL ??
        row.unrealizedPNL,
      gainLossPercentage:
        row.gainLossPercentage ??
        row.unrealisedGainLossPercentage ??
        row.unrealisedPNLPercentage ??
        row.roi,
      folioNumber: row.folioNumber ?? row.folioNo ?? row.folio,
      cagr: resolveHoldingCagr(row),
      xirr: row.xirr ?? meta.xirr ?? "-",
      days: resolveHoldingAverageDays(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.meta ?? {};
  const schemeName = getPmsSchemeName(row);
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.averagePrice ??
    meta.avgBuyPrice;
  const purchaseNav =
    row.purchaseNAV ??
    row.purchaseNav ??
    row.purchasePrice ??
    row.nav ??
    meta.purchaseNAV ??
    meta.purchasePrice;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date,
      ),
      currentDate: resolveCurrentDateOrToday(row),
      purchaseNAV: purchaseNav ?? "-",
      avgBuyPrice: avgBuy ?? "-",
      saleValue: resolveReportSaleAmount(row) ?? row.saleValue ?? row.amount,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.gainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage ??
        row.gainLossPercentage,
      cagr: resolveHoldingCagr(row),
      gainLoss: undefined,
    }),
  );
};

const flattenPmsHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getPmsSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;

    const holdingCurrentAmount =
      h.currentAmount ?? h.currentValue ?? undefined;
    const holdingAsOfDate = h.asOfDate ?? null;
    const holdingBenchmarks = pickHoldingBenchmarkFields(h);

    for (const t of list) {
      const meta = t.metadata ?? t.metaData ?? t.meta ?? {};
      const rawType =
        t.transType ?? t.txnType ?? t.transactionType ?? t.transactiontype ?? "";
      const isSell = isSellTxn(t);

      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...holdingBenchmarks,
            ...h,
            ...t,
            schemeName: t.schemeName ?? schemeName,
            folioNumber: t.folioNumber ?? h.folioNumber ?? h.folioNo,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transType: formatBuySellTxnTypeLabel(String(rawType ?? "")),
            transactionDate: formatEpochDate(
              t.transactionDate ?? t.transactiondate ?? t.date ?? t.trxnDate,
            ),
            asOfDate: formatEpochDate(
              t.asOfDate ?? meta.asOfDate ?? holdingAsOfDate,
            ),
            currentDate: resolveCurrentDateOrToday(t),
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: holdingCurrentAmount ?? t.currentAmount ?? t.currentValue,
            unrealisedPnL:
              t.unrealisedPnL ??
              t.unrealisedPNL ??
              t.unrealisedGainLoss ??
              (isSell ? "-" : h.gainLoss),
            unrealisedPnLPercentage:
              t.unrealisedPnLPercentage ??
              t.unrealisedPNLPercentage ??
              t.unrealisedGainLossPercentage ??
              (isSell ? "-" : h.gainLossPercentage),
            realisedPnL:
              t.realisedPnL ??
              t.realisedPnl ??
              t.realisedPNL ??
              t.realizedPnl ??
              "-",
            realisedPNLPercentage:
              t.realisedPNLPercentage ??
              t.realisedPnLPercentage ??
              t.realizedPNLPercentage ??
              "-",
            days: resolveHoldingAverageDays({ ...h, ...t }),
            cagr: t.cagr ?? h.cagr ?? resolveHoldingCagr(h),
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  return {
    ...normalized,
    purchaseDate: formatEpochDate(
      row.inceptionDate ??
        row.purchaseDate ??
        row.metadata?.inceptionDate ??
        row.metadata?.purchaseDate,
    ),
    currentDate: resolveCurrentDateOrToday(row),
    costValue:
      normalized.investedAmount ?? normalized.costValue ?? normalized.amount,
    currentValue:
      normalized.currentAmount ?? normalized.currentValue ?? normalized.marketValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage: normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getPmsSchemeName(row);
  const txnDateRaw =
    row.transactionDate ?? row.transactiondate ?? row.date ?? null;
  return {
    ...normalizeRealizedRow({ ...row, schemeName }),
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    purchaseDate: txnDateRaw
      ? formatEpochDate(txnDateRaw)
      : formatEpochDate(row.purchaseDate),
    currentDate: resolveCurrentDateOrToday(row),
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: row.currentAmount ?? row.currentValue,
  };
};

const extractPmsHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractReportPayloadRows = (payload: unknown): Record<string, any>[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const p = payload as Record<string, any>;
  if (Array.isArray(p.holdings)) return p.holdings;
  if (Array.isArray(p.data)) return p.data;
  if (Array.isArray(p.data?.data)) return p.data.data;
  if (Array.isArray(p.response)) return p.response;
  if (Array.isArray(p.rows)) return p.rows;
  return [];
};

const reportBackendFieldOrDash = (value: unknown): unknown => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const pickBackendReportField = (
  row: Record<string, any>,
  ...keys: string[]
): unknown => {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return undefined;
};

const parsePmsReportNumeric = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value === null || value === undefined || value === "" || value === "-") {
    return 0;
  }
  const cleaned = String(value).replace(/[₹,\s%]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const flattenSectorCapReportRows = (
  rows: Record<string, any>[],
): Record<string, any>[] => {
  const out: Record<string, any>[] = [];
  for (const item of rows) {
    if (!item || typeof item !== "object") continue;
    const parentMember =
      (typeof item.memberName === "string" && item.memberName.trim()) ||
      (typeof item.member_name === "string" && item.member_name.trim()) ||
      (typeof item.member === "string" && item.member.trim()) ||
      "";
    const nested = item.sectors ?? item.sectorData ?? item.sectorList;
    if (Array.isArray(nested) && nested.length > 0) {
      for (const s of nested) {
        if (!s || typeof s !== "object") continue;
        out.push({
          ...s,
          memberName:
            (typeof s.memberName === "string" && s.memberName.trim()) ||
            parentMember,
          userId: s.userId ?? item.userId,
        });
      }
      continue;
    }
    out.push(item);
  }
  return out;
};

const pickPmsMarketCapFromRow = (row: Record<string, any>): unknown =>
  pickBackendReportField(row, "marketCap", "category", "market_cap");

const pickPmsExposureFromRow = (row: Record<string, any>): unknown =>
  pickBackendReportField(
    row,
    "exposure",
    "exposurePct",
    "percentageShare",
    "exposure_pct",
  );

const sortPmsBreakdownRowsByValue = (
  rows: Record<string, any>[],
): Record<string, any>[] =>
  [...rows].sort(
    (a, b) => parsePmsReportNumeric(b.value) - parsePmsReportNumeric(a.value),
  );

const mapPmsMarketCapReportRows = (
  rows: Record<string, any>[],
  schemeLabel: string,
): Record<string, any>[] =>
  rows.map((row) => ({
    ...row,
    schemeName: reportBackendFieldOrDash(
      pickBackendReportField(row, "schemeName") ?? schemeLabel,
    ),
    marketCap: reportBackendFieldOrDash(pickPmsMarketCapFromRow(row)),
    isin: reportBackendFieldOrDash(
      pickBackendReportField(row, "isin", "ISIN", "isinCode", "isin_code"),
    ),
    stockName: reportBackendFieldOrDash(
      pickBackendReportField(
        row,
        "stockName",
        "stockname",
        "stock_name",
        "Security",
        "security",
      ),
    ),
    exposurePct: reportBackendFieldOrDash(pickPmsExposureFromRow(row)),
    memberName: reportBackendFieldOrDash(row.memberName),
    userId: row.userId,
    value:
      typeof row.value === "number" && Number.isFinite(row.value)
        ? row.value
        : reportBackendFieldOrDash(row.value),
  }));

const mapPmsSectorReportRows = (
  rows: Record<string, any>[],
  schemeLabel: string,
): Record<string, any>[] =>
  rows.map((row) => ({
    ...row,
    schemeName: reportBackendFieldOrDash(
      pickBackendReportField(row, "schemeName") ?? schemeLabel,
    ),
    isin: reportBackendFieldOrDash(
      pickBackendReportField(row, "isin", "ISIN", "isinCode", "isin_code"),
    ),
    stockName: reportBackendFieldOrDash(
      pickBackendReportField(
        row,
        "stockName",
        "stockname",
        "stock_name",
        "Security",
        "security",
      ),
    ),
    sector: reportBackendFieldOrDash(row.sector),
    percentageShare: reportBackendFieldOrDash(pickPmsExposureFromRow(row)),
    memberName: reportBackendFieldOrDash(row.memberName),
    userId: row.userId,
    value:
      typeof row.value === "number" && Number.isFinite(row.value)
        ? row.value
        : reportBackendFieldOrDash(row.value),
  }));

const formatConsolidatedClosingValue = (value: unknown): number | "-" => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[₹,\s]/g, ""));
  if (!Number.isFinite(num)) return "-";
  return num;
};

const formatConsolidatedPercent = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[%,\s()]/g, ""));
  if (!Number.isFinite(num)) return "-";
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `(${abs})` : abs;
};

const isPmsNonPerformanceRow = (label: string): boolean => {
  const normalized = label.trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === "cash" || normalized.includes("cash")) return true;
  if (normalized.includes("dividend") && normalized.includes("receivable")) {
    return true;
  }
  if (normalized.includes("interest") && normalized.includes("receivable")) {
    return true;
  }
  return false;
};

const flattenConsolidatedReportRows = (
  schemes: Record<string, any>[],
): Record<string, any>[] => {
  const out: Record<string, any>[] = [];
  for (const scheme of schemes) {
    if (!scheme || typeof scheme !== "object") continue;
    const schemeName = String(scheme.schemeName ?? "").trim() || "-";
    const memberName =
      (typeof scheme.memberName === "string" && scheme.memberName.trim()) ||
      (typeof scheme.member_name === "string" && scheme.member_name.trim()) ||
      "";
    const userId =
      scheme.userId ?? scheme.userid ?? scheme.user_id ?? scheme.memberId;
    out.push({
      name: schemeName,
      closingValue: formatConsolidatedClosingValue(scheme.closingValue),
      mtdPercent: formatConsolidatedPercent(scheme.schemeMtdPercent),
      fytdPercent: formatConsolidatedPercent(scheme.schemeFytdPercent),
      oneYearPercent: formatConsolidatedPercent(scheme.schemeOneYearPercent),
      threeYearPercent: formatConsolidatedPercent(
        scheme.schemeThreeYearPercent,
      ),
      memberName: memberName || scheme.memberName,
      ...(userId != null && userId !== "" ? { userId } : {}),
      _rowKind: "scheme",
      schemeName,
    });
    const stocks = Array.isArray(scheme.stocks) ? scheme.stocks : [];
    for (const stock of stocks) {
      if (!stock || typeof stock !== "object") continue;
      const security =
        String(stock.security ?? stock.stockName ?? stock.name ?? "").trim() ||
        "-";
      const suppressPercents = isPmsNonPerformanceRow(security);
      const stockMemberName =
        (typeof stock.memberName === "string" && stock.memberName.trim()) ||
        memberName ||
        "";
      out.push({
        name: security,
        closingValue: formatConsolidatedClosingValue(stock.closingValue),
        mtdPercent: suppressPercents
          ? "-"
          : formatConsolidatedPercent(stock.mtdPercent),
        fytdPercent: suppressPercents
          ? "-"
          : formatConsolidatedPercent(stock.fytdPercent),
        oneYearPercent: suppressPercents
          ? "-"
          : formatConsolidatedPercent(stock.oneYearPercent),
        threeYearPercent: suppressPercents
          ? "-"
          : formatConsolidatedPercent(stock.threeYearPercent),
        memberName: stockMemberName || stock.memberName || scheme.memberName,
        ...(userId != null && userId !== "" ? { userId } : {}),
        _rowKind: "stock",
        schemeName,
        sector: stock.sector,
      });
    }
  }
  return out;
};

const parsePmsCapOrSectorRows = (
  payload: any,
  kind: "cap" | "sector",
): any[] => {
  const rawRows = flattenSectorCapReportRows(extractReportPayloadRows(payload));
  const mapped =
    kind === "cap"
      ? mapPmsMarketCapReportRows(rawRows, "All Schemes")
      : mapPmsSectorReportRows(rawRows, "All Schemes");
  return sortPmsBreakdownRowsByValue(mapped);
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const info =
    payload?.totalHoldingInfo ??
    payload?.totalHolding ??
    payload?.data?.totalHoldingInfo ??
    null;
  return normalizePmsReportTotalHoldingInfo(
    info && typeof info === "object" ? info : null,
  );
};

export type ParsedPmsReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parsePmsReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedPmsReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding": {
      const holdings = extractPmsHoldings(payload);
      rows = filterPmsHoldings(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    }
    case "Realized": {
      const holdings = extractPmsHoldings(payload);
      rows = filterRealizedTxnRows(flattenPmsHoldingsTransactions(holdings)).map(
        normalizeRealizedRow,
      );
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractPmsHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPmsHoldings(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenPmsHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractPmsHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenPmsHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case PMS_CAP_REPORT: {
      rows = parsePmsCapOrSectorRows(payload, "cap");
      totalHoldingInfo = null;
      totalRecords = rows.length;
      break;
    }
    case PMS_SECTOR_REPORT: {
      rows = parsePmsCapOrSectorRows(payload, "sector");
      totalHoldingInfo = null;
      totalRecords = rows.length;
      break;
    }
    case PMS_CONSOLIDATED_REPORT: {
      rows = flattenConsolidatedReportRows(extractReportPayloadRows(payload));
      totalHoldingInfo = null;
      totalRecords = rows.length;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergePmsTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.schemeName}-${r.transactionDate}-${r.amount}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.schemeName}-${row.transactionDate}-${row.amount}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
